import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';

interface Plan {
  _id: string;
  name: string;
  description: string;
  unit_name: string;
  token_rate: number;
  message_rate: number;
  currency: string;
  billing_interval: string;
  free_tokens_limit: number;
  free_messages_limit: number;
}

const PaymentSetupPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Get parameters from URL
  const planId = searchParams.get('plan');
  const setupUrlParam = searchParams.get('setupUrl');

  useEffect(() => {
    if (setupUrlParam) {
      // If we have a setup URL, redirect immediately to Xendit
      console.log('Payment setup - Redirecting to Xendit:', setupUrlParam);
      window.location.href = setupUrlParam;
      return;
    }

    if (!planId) {
      setError('No plan specified');
      setLoading(false);
      return;
    }

    fetchPlanDetails();
  }, [planId, setupUrlParam]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      console.log('Payment setup - Fetching plan details for:', planId);

      // Fetch plan details
      const plansResponse = await fetch(API_CONFIG.ENDPOINTS.BILLING_PLANS, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!plansResponse.ok) {
        throw new Error('Failed to fetch plans');
      }

      const plansData = await plansResponse.json();
      const selectedPlan = plansData.data.find((p: Plan) => p._id === planId);

      if (!selectedPlan) {
        throw new Error('Plan not found');
      }

      console.log('Payment setup - Found plan:', selectedPlan.name);
      setPlan(selectedPlan);

      // Note: Payment setup is handled by the subscription init endpoint
      // This page just displays plan info and waits for redirect

    } catch (err) {

      setError(err instanceof Error ? err.message : 'Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  // Payment setup is handled by the subscription init endpoint
  // This page just displays plan information before redirect

  const handleProceedToPayment = () => {
    if (!setupUrl) return;

    setIsRedirecting(true);
    console.log('Redirecting to Xendit payment page:', setupUrl);

    // Redirect to Xendit's hosted payment page
    window.location.href = setupUrl;
  };

  const handleCancel = () => {
    navigate('/panel/subscription');
  };

  const formatCurrency = (amount: number, currency: string = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="payment-setup-page">
        <div className="payment-setup-container">
          <div className="loading-spinner">
            <h2>Loading Plan Details...</h2>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-setup-page">
        <div className="payment-setup-container">
          <div className="error-message">
            <h2>Payment Setup Error</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/panel/subscription')} className="btn btn-primary">
                Back to Subscription
              </button>
              <button onClick={fetchPlanDetails} className="btn btn-secondary">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="payment-setup-page">
        <div className="payment-setup-container">
          <div className="error-message">
            <h2>Plan Not Found</h2>
            <p>The selected plan could not be found.</p>
            <button onClick={() => navigate('/panel/subscription')} className="btn btn-primary">
              Back to Subscription
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-setup-page">
      <div className="payment-setup-container">
        <div className="payment-setup-header">
          <h1>Complete Your Subscription</h1>
          <p>You're about to subscribe to the <strong>{plan.name}</strong> plan</p>
        </div>

        <div className="plan-summary-card">
          <div className="plan-summary-header">
            <h2>{plan.name}</h2>
            <p>{plan.description}</p>
          </div>

          <div className="plan-summary-features">
            <div className="feature-item">
              <span className="feature-icon">✓</span>
              <span>{plan.free_tokens_limit.toLocaleString()} tokens free per month</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✓</span>
              <span>{plan.free_messages_limit.toLocaleString()} messages free per month</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💰</span>
              <span>{formatCurrency(plan.token_rate * 1000)} per additional 1K tokens</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💬</span>
              <span>{formatCurrency(plan.message_rate)} per additional message</span>
            </div>
          </div>

          <div className="plan-summary-note">
            <p><strong>Important:</strong> You'll only be charged for usage beyond the free limits. Billing occurs monthly based on actual usage.</p>
          </div>
        </div>

        <div className="payment-setup-info">
          <h3>What happens next?</h3>
          <ol>
            <li>You'll be redirected to our secure payment partner (Xendit)</li>
            <li>Set up your preferred payment method (card, e-wallet, etc.)</li>
            <li>Complete a small verification payment (refundable)</li>
            <li>Your subscription will be activated automatically</li>
            <li>You can start using the platform with your free limits</li>
          </ol>
        </div>

        <div className="payment-setup-actions">
          {isRedirecting ? (
            <div className="redirecting-message">
              <div className="spinner"></div>
              <p>Redirecting to secure payment page...</p>
            </div>
          ) : setupUrl ? (
            <div className="action-buttons">
              <button
                onClick={handleProceedToPayment}
                className="btn btn-primary btn-large"
              >
                Proceed to Payment Setup
              </button>
              <button
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="loading-message">
              <div className="spinner"></div>
              <p>Setting up payment...</p>
            </div>
          )}
        </div>

        <div className="payment-setup-security">
          <div className="security-badge">
            <span className="security-icon">🔐</span>
            <span>Secure payment processing by Xendit</span>
          </div>
          <p>Your payment information is encrypted and secure. We never store your payment details.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSetupPage;


